import {
  COMMUNITY_AGE_STATEMENT_VERSION,
  COMMUNITY_IDENTITY_ACQUISITION_PATH,
  buildCommunityIdentityCompletion,
} from './communityIdentityContract.js';
import { normalizeAuthCaptchaToken } from './authTurnstile.js';

export class PublicEmailSignupValidationError extends Error {
  constructor(message, code = 'invalid_public_email_signup') {
    super(message);
    this.name = 'PublicEmailSignupValidationError';
    this.code = code;
    this.status = 400;
  }
}

export class PublicEmailSignupRequestError extends Error {
  constructor(message, code = 'public_email_signup_failed', status = 500) {
    super(message);
    this.name = 'PublicEmailSignupRequestError';
    this.code = code;
    this.status = status;
  }
}

export const buildPublicEmailSignupPayload = (input = {}) => {
  const email = typeof input.email === 'string' ? input.email.trim() : '';
  const password = typeof input.password === 'string' ? input.password : '';
  const captchaToken = normalizeAuthCaptchaToken(input.captchaToken);

  if (!email) {
    throw new PublicEmailSignupValidationError(
      'Inserisci il tuo indirizzo email.',
      'missing_email',
    );
  }

  if (!password.trim()) {
    throw new PublicEmailSignupValidationError(
      'Inserisci la tua password.',
      'missing_password',
    );
  }

  const identity = buildCommunityIdentityCompletion({
    handle: input.handle ?? input.publicHandle ?? input.public_handle,
    ageConfirmed: input.ageConfirmed,
    statementVersion: COMMUNITY_AGE_STATEMENT_VERSION,
    // This value is fixed by the shared contract and is revalidated server-side.
    acquisitionPath: COMMUNITY_IDENTITY_ACQUISITION_PATH.EMAIL_SIGNUP,
  });

  return {
    email,
    password,
    handle: identity.handle,
    ageConfirmed: true,
    statementVersion: identity.statementVersion,
    ...(captchaToken ? { captchaToken } : {}),
  };
};

const parseResponseBody = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export const signUpWithPublicEmail = async (input, options = {}) => {
  const payload = buildPublicEmailSignupPayload(input);
  const fetchImpl = options.fetchImpl || globalThis.fetch;

  if (typeof fetchImpl !== 'function') {
    throw new PublicEmailSignupRequestError(
      'Servizio di registrazione non disponibile.',
      'public_email_signup_unavailable',
      503,
    );
  }

  const response = await fetchImpl('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new PublicEmailSignupRequestError(
      body?.message || 'Impossibile completare la registrazione.',
      body?.error || 'public_email_signup_failed',
      response.status,
    );
  }

  return {
    user: body?.user || null,
    session: body?.session || null,
    communityIdentity: body?.communityIdentity || null,
    confirmationRequired: body?.confirmationRequired !== false,
  };
};
